// src/components/HeroSlider.tsx
import React, { useState, useEffect } from 'react';

interface AdBanner {
  id: number;
  imageUrl: string;
  title: string;
  description: string;
  ctaText: string;
  linkUrl: string;
  altText: string;
}

const HeroSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [adBanners, setAdBanners] = useState<AdBanner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = 'http://localhost:2026';

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/banners`);
        if (!response.ok) throw new Error('Failed to fetch banners');
        const data: AdBanner[] = await response.json();
        setAdBanners(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanners();
  }, []);

  useEffect(() => {
    if (adBanners.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev === adBanners.length - 1 ? 0 : prev + 1));
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [adBanners.length]);

  return (
    // section tag is better for SEO than div for main content blocks
    <section 
      aria-label="Promotion Banners" 
      className="relative w-full h-auto mt-4 lg:mt-6 overflow-hidden rounded-xl shadow-lg group bg-gray-50"
    >
      {adBanners.length > 0 ? (
        <div 
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {adBanners.map((banner, index) => (
            <article 
              key={banner.id} 
              className="w-full flex-shrink-0 relative overflow-hidden"
              style={{ aspectRatio: '16 / 6' }}
              aria-hidden={currentSlide !== index}
            >
              <img
                src={`${API_BASE_URL}${banner.imageUrl}`}
                alt={banner.altText || banner.title} // SEO: Ensures alt text always exists
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"} // SEO: First image loads fast, others lazy-load
                fetchPriority={index === 0 ? "high" : "low"} // SEO: Tells browser to prioritize first banner
              />

              {/* TEXT OVERLAY */}
              <div className="absolute inset-0 to-transparent flex items-center p-6 lg:p-12 text-white bg-gradient-to-r from-black/40 via-transparent">
                <div className="max-w-xs lg:max-w-lg">
                  <h2 className="text-xl lg:text-4xl font-extrabold mb-1 lg:mb-2 uppercase drop-shadow-lg">
                    {banner.title}
                  </h2>
                  {banner.description && (
                    <p className="text-sm lg:text-xl drop-shadow-md hidden md:block opacity-90">
                      {banner.description}
                    </p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center h-[200px] text-gray-400" aria-live="polite">
          {isLoading ? 'Loading specialized offers...' : 'No current offers found'}
        </div>
      )}

      {/* Pagination Dots */}
      <nav 
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2"
        aria-label="Banner Navigation"
      >
        {adBanners.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={currentSlide === index}
            className={`transition-all duration-300 h-1.5 rounded-full ${
              currentSlide === index ? 'bg-[#0f6f5c] w-8' : 'bg-white/50 w-2'
            }`}
          />
        ))}
      </nav>
    </section>
  );
};

export default HeroSlider;